<script setup>
import { ref, onMounted } from 'vue';
import { useScrollAnimations } from '@/composables/useScrollAnimations';
import managementDashboardImg from '@/assets/management-platform-screenshot.png';
import trainingPlatformImg from '@/assets/training-platform-screenshot.png';
import certificatePreview from '@/assets/certificatePreview.png';

const { animateInView, slideUp } = useScrollAnimations();
const sectionRef = ref(null);

onMounted(() => {
    if (sectionRef.value) {
        animateInView(sectionRef.value, () => {
            slideUp(sectionRef.value);
        });
    }
});
</script>

<template>
    <div class="py-24 relative opacity-0" ref="sectionRef">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">

            <!-- Header -->
            <div class="text-center mb-20 max-w-3xl mx-auto">
                <h2 class="text-3xl font-bold tracking-tight text-white sm:text-5xl mb-6"
                    v-html="$t('landingPage.v2.howItWorks.header.title')">
                </h2>
                <p class="text-lg text-gray-400 leading-relaxed">
                    {{ $t('landingPage.v2.howItWorks.header.subtitle') }}
                </p>
            </div>

            <!-- Grid -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-12">

                <!-- Column 1: Management Dashboard -->
                <div class="flex flex-col">
                    <!-- Image Area -->
                    <div
                        class="mb-8 relative rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-gray-900  group">
                        <img :src="managementDashboardImg" alt="Management Dashboard"
                            class="w-full h-full object-cover transition-transform duration-500 ">
                    </div>

                    <!-- Features -->
                    <div class="space-y-6">
                        <div class="flex gap-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-bold text-white">{{
                                    $t('landingPage.v2.howItWorks.columns.dashboard.title') }}</h4>
                                <p class="text-gray-400 text-sm mt-1">{{
                                    $t('landingPage.v2.howItWorks.columns.dashboard.desc') }}</p>
                            </div>
                        </div>
                        <div class="flex gap-4">
                            <!-- Globe Icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S12 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S12 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-bold text-white">{{
                                    $t('landingPage.v2.howItWorks.columns.dashboard.multilingual.title') }}</h4>
                                <p class="text-gray-400 text-sm mt-1">{{
                                    $t('landingPage.v2.howItWorks.columns.dashboard.multilingual.desc') }}
                                </p>
                            </div>
                        </div>
                        <div class="flex gap-4">
                            <!-- Scale/Balance/Law Icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                                <!-- Using Info icon or Gavel, sticking to standard icons -->
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2.25 2.25L12 7.5 21 -3"
                                    style="display:none" />
                                <!-- Let's use a "Gavel" or similar for GDPR if available, or just a Check/Shield -->
                            </svg>
                            <div>
                                <h4 class="text-lg font-bold text-white">{{
                                    $t('landingPage.v2.howItWorks.columns.dashboard.gdpr.title') }}</h4>
                                <p class="text-gray-400 text-sm mt-1">{{
                                    $t('landingPage.v2.howItWorks.columns.dashboard.gdpr.desc') }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Column 2: Scenarios -->
                <div class="flex flex-col">
                    <!-- Image Area -->
                    <div
                        class="mb-8 relative rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-gray-900 group">
                        <img :src="trainingPlatformImg" alt="Training Scenarios"
                            class="w-full h-full object-cover transition-transform duration-500 ">
                    </div>

                    <!-- Features -->
                    <div class="space-y-6">
                        <div class="flex gap-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-bold text-white">{{
                                    $t('landingPage.v2.howItWorks.columns.scenarios.title') }}</h4>
                                <p class="text-gray-400 text-sm mt-1">{{
                                    $t('landingPage.v2.howItWorks.columns.scenarios.desc') }}</p>
                            </div>
                        </div>
                        <div class="flex gap-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-bold text-white">{{
                                    $t('landingPage.v2.howItWorks.columns.scenarios.impact.title') }}</h4>
                                <p class="text-gray-400 text-sm mt-1">{{
                                    $t('landingPage.v2.howItWorks.columns.scenarios.impact.desc') }}</p>
                            </div>
                        </div>
                        <div class="flex gap-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-bold text-white">{{
                                    $t('landingPage.v2.howItWorks.columns.scenarios.updates.title') }}</h4>
                                <p class="text-gray-400 text-sm mt-1">{{
                                    $t('landingPage.v2.howItWorks.columns.scenarios.updates.desc') }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Column 3: Certificates -->
                <div class="flex flex-col">
                    <!-- Image Area -->
                    <div class="mb-8 relative rounded-xl overflow-hidden flex items-center justify-center">
                        <!-- Placeholder for Certificate -->
                        <img :src="certificatePreview" alt="Training Scenarios"
                            class="w-full h-full object-cover transition-transform duration-500 ">
                    </div>

                    <!-- Features -->
                    <div class="space-y-6">
                        <div class="flex gap-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-bold text-white">{{
                                    $t('landingPage.v2.howItWorks.columns.certificates.title') }}</h4>
                                <p class="text-gray-400 text-sm mt-1">{{
                                    $t('landingPage.v2.howItWorks.columns.certificates.desc') }}</p>
                            </div>
                        </div>
                        <div class="flex gap-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                            </svg>
                            <div>
                                <div>
                                    <h4 class="text-lg font-bold text-white">{{
                                        $t('landingPage.v2.howItWorks.columns.certificates.reports.title') }}</h4>
                                    <p class="text-gray-400 text-sm mt-1">{{
                                        $t('landingPage.v2.howItWorks.columns.certificates.reports.desc') }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="flex gap-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                            </svg>
                            <div>
                                <div>
                                    <h4 class="text-lg font-bold text-white">{{
                                        $t('landingPage.v2.howItWorks.columns.certificates.compliance.title') }}</h4>
                                    <p class="text-gray-400 text-sm mt-1">{{
                                        $t('landingPage.v2.howItWorks.columns.certificates.compliance.desc') }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<style scoped></style>
